import {
  popupStore,
  userInfoStore,
  roundInfoStore,
  userCountStore,
  gameResultStore,
  gameConfigStore,
  notificationStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  BetStatus,
  RoundStatus,
  applicationStore,
  tableBetHistoryStore,
  freeBetStore,
  betStore,
} from '@pascal/pascal-core';
import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { BetResponseCode } from '@pascal/pascal-core/shared/core/enums/ResponseCodes';
import { oddStore } from '../store/oddStore';
import { gameStore } from '../store/gameStore';
import { reBetStore } from '../store/reBetStore';
import { doBetStore } from '../store/doBetStore';
import { loadingStore } from '../store/loadingStore';
import { winnersStore } from '../store/winnersStore';
import { totalBetStore } from '../store/totalBetStore';
import { quickBetStore } from '../store/quickBetsStore';
import { roundTimeStore } from '../store/roundTimeStore';
import { liveBetsDataStore } from '../store/liveBetsDataStore';
import { delay } from '../helpers/delay';
import { pigIndexes } from '../enums/pigColors';
import { gameDrawer } from '../game/drawers/gameDrawer';
import { balanceChecker } from '../helpers/balanceChecker';
import { backgroundDrawer } from '../game/drawers/background';
import { randomActionGenerator } from '../helpers/randomAnctionGenerator';
import { worker } from '../index';
import { gameLiveConnection } from './GameConnection';
import { gameAnimations } from './GameAnimationService';
import { soundService, soundTypes } from './soundService';
import { liveConnection } from '../core';

let timeOutId;

class GameService {
  delayCancellation = null;

  handleConnectionStatusInterval = null;

  handleConnectionStatusTimeOut = null;

  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    precision,
    roundInfo,
    currentBets,
    userFreebets,
    currencyCode,
    autoCashoutOdds,
    partnerWageringOdd,
  }) {
    // const { [DefaultQueryParams.UserId]: userId, [DefaultQueryParams.Mode]: mode } = parseQuery(
    //   window.location.search
    // );
    gameAnimations.resetAnimations();
    gameAnimations.killBackgroundLoop();
    gameAnimations.resetBackground();
    oddStore.resetOddData();

    if (partnerWageringOdd) {
      oddStore.setPartnerWageringOdd(partnerWageringOdd);
    }
    // if (!userId || mode === GameModes.Fun) {
    //   applicationStore.setIsDemo(true);
    // }
    if (
      roundInfo.status === RoundStatus.OnHold ||
      (roundInfo.status === RoundStatus.Started && roundInfo.second < 0)
    ) {
      gameAnimations.hideWall();
      if (!gameAnimations.isAnimationStarted) {
        gameAnimations.backgroundLoop();
      }
      gameAnimations.runPigByIndex(pigIndexes.red);
      gameAnimations.runPigByIndex(pigIndexes.yellow);
      gameAnimations.runPigByIndex(pigIndexes.blue);
      gameAnimations.wolfRun();
      const { savedPigs } = roundInfo.pigs;
      if (savedPigs && savedPigs.length) {
        savedPigs.forEach(({ pigIndex, odd }) => {
          doBetStore.setCloudToRed(pigIndex);
          oddStore.setOddData(pigIndex, odd);
          gameAnimations.catchPigByIndex(pigIndex);
        });
      }
      if (roundInfo.bonus) {
        gameStore.setMultiplierData(roundInfo.bonus);
      }
      if (roundInfo.status === RoundStatus.OnHold || roundInfo.status === RoundStatus.Started) {
        gameStore.setIsBetsClosed(true);
        doBetStore.setIsAllNexRoundBet(true);
        doBetStore.setIsAllNexRoundAutoCashOut(true);
        liveBetsDataStore.setLiveBetsData(currentBets);
        !applicationStore.isDemo && doBetStore.mapRealBetsToPigsData(userFreebets);
      } else if (roundInfo.status === RoundStatus.OnFinish) {
        gameAnimations.resetAnimations();
        gameAnimations.killBackgroundLoop();
        gameAnimations.resetBackground();
        oddStore.resetOddData();
      }
    }
    roundInfoStore.setRoundInfo(roundInfo);
    gameResultStore.setGameResults(results);
    gameConfigStore.setLimits(limits);
    quickBetStore.setQuickBets(limits.quickBets);
    doBetStore.setAutoCashOutQuickOdds(autoCashoutOdds);
    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      const time = Math.floor(Math.abs(roundInfo.second));
      roundTimeStore.setTime(time);
      this.sendWorker(time);
    } else {
      this.sendWorker();
      roundTimeStore.setTime(null);
    }
    loadingStore.updateLoading({ isPixiLoaded: true });
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    setTimeout(() => {
      popupStore.toggleReconnect(false);
    }, 1000);
    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
    userCountStore.setUsersCount(currentBets.length);
    winnersStore.resetWinnersData([]);
  }

  handleDoBetResult(data) {
    liveBetsDataStore.updateLiveBetsData(data);
    userCountStore.setUsersCount(userCountStore.usersCount + 1);
    // const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
    if (+userInfoStore.userId === data.userId) {
      freeBetStore.handleFreeBetDoBetResult(data?.freeBetId);
      doBetStore.setDoBetResult(data);
      totalBetStore.setTotalBet(totalBetStore.totalBet + data.amount);
    }
  }

  handleDoBetResultValidation(data) {
    doBetStore.resetPigsDataOnResultReport(data?.betData?.index);
    notificationStore.setNotificationType(data.responseCode);
    setTimeout(() => {
      if (notificationStore.notificationCode !== BetResponseCode.UnderMaintenanceNote) {
        notificationStore.setNotificationType(null);
      }
    }, 1000);
  }

  handleRoundStart({ data: { results, startedRound } }) {
    oddStore.setIsShowCloud(false);
    doBetStore.setIsAllNexRoundBet(false);
    freeBetStore.handleFreeBetEndedData();
    const { balance } = userInfoStore;
    // please don't call me if you have any question
    let prevAcceptedBetAmounts = 0;
    const updatedPigsData = doBetStore.pigs.map((pig, index) => {
      if (pig.betAmount > balance) {
        return doBetStore.handleShowNotification(pig);
      }
      if (prevAcceptedBetAmounts < balance) {
        const restBalance = balance - (index ? prevAcceptedBetAmounts : 0);
        if (pig.betAmount < restBalance) {
          prevAcceptedBetAmounts += pig.betAmount;
          return pig;
        }
        return doBetStore.handleShowNotification(pig);
      }
      return pig;
    });

    doBetStore.setPigsData(updatedPigsData);
    gameStore.setIsPigMultiplierInvisible(false);
    gameStore.setMultiplierData({});

    reBetStore.isReBetClick &&
      doBetStore.pigs.forEach((item) => (item.betAmount ? balanceChecker() : null));
    oddStore.resetOddData();
    doBetStore.setCloudToBlue();
    gameStore.setIsBetsClosed(false);
    doBetStore.setIsAllNexRoundAutoCashOut(false);
    roundInfoStore.setRoundId(startedRound.roundId);
    gameResultStore.setGameResults(results);
    this.sendWorker(roundInfoStore.duration);
    if (loadingStore.isPixiLoaded) {
      gameAnimations.resetAnimations();
    }
  }

  handleResultReport({ data: { resultReportData } }) {
    const { crashedItemsInfo, bonus, currentItemsInfo } = resultReportData;
    if (bonus) {
      gameStore.setMultiplierData(bonus);
    }

    currentItemsInfo.forEach(({ index, currentOdd }) => {
      oddStore.setOddData(index, currentOdd);
    });

    if (loadingStore.isPixiLoaded) {
      if (randomActionGenerator(resultReportData.seconds)) {
        soundService.play(soundTypes.tryingToCatch);
        gameAnimations.wolfCatching().then(() => {
          if (gameAnimations.isAnimationStarted) {
            gameAnimations.wolfRun();
          }
        });
      }

      if (randomActionGenerator(resultReportData.seconds)) {
        gameAnimations.whiteWolf();
      }
    }

    const findResult = doBetStore.pigs.some(
      (pig) =>
        pig.isBetAccepted &&
        pig.betId &&
        +pig.autoCashOutOdd <= +oddStore.oddData[pig.id] &&
        pig.autoCashOutOdd
    );

    if (findResult) {
      // if (applicationStore.isDemo) {
      //   doBetStore.setDemoAutoCashOutWinData();
      // } else {
      doBetStore.setAutoCashOutWinData(resultReportData);
      // }
    }

    if (crashedItemsInfo?.length) {
      if (!freeBetStore.activeFreeBet?.id) {
        doBetStore.resetPigFreeBetId(crashedItemsInfo[0]?.index);
      }
      crashedItemsInfo.forEach(({ index, odd }) => {
        doBetStore.setSavedPigs(index);
        const updatedPigsData = doBetStore.pigs.map((pig) =>
          pig.id === index ? { ...pig, isDisabled: false } : pig
        );

        doBetStore.setPigsData(updatedPigsData);
        oddStore.setOddData(index, odd);
        doBetStore.resetPigsDataOnResultReport(index, odd);
        if (loadingStore.isPixiLoaded) {
          soundService.play(soundTypes.scream);
          gameAnimations.catchPigByIndex(index);
        }
        oddStore.setOddData(index, odd);
        setTimeout(() => {
          if (betStore?.repliedBetData) {
            gameAnimations.hidePigByIndex(index);
          }
        }, 850);
      });
    }
    freeBetStore.handleFreeBetEndedData();
  }

  handleRoundFin({ data: { finishedRound, bets } }) {
    const winningIds = bets.filter((bet) => bet.winAmount > 0);
    const demoWinMarkets = liveBetsDataStore.liveBetsData.filter(
      (item) => item.status === BetStatus.Win
    );
    const { userId } = userInfoStore;
    const userBets = bets.find((bet) => +bet.userId === +userId);
    gameStore.setIsPigMultiplierInvisible(true);
    if (userBets && !applicationStore.isDemo) {
      tableBetHistoryStore.handleBetHistoryUpdate();
    }
    (winningIds.length || demoWinMarkets.length) &&
      winnersStore.setWinnersData([...winningIds, ...demoWinMarkets]);
    totalBetStore.setTotalBet(0);
    doBetStore.resetPigsDataOnRoundFin();
    liveBetsDataStore.setLiveBetsData([]);
    userCountStore.setUsersCount(0);
    if (!betStore.repliedBetData) {
      const updatedResults = [...gameResultStore.gameResults].map((item) =>
        +item.roundId === +finishedRound.id
          ? { ...finishedRound, roundId: finishedRound.id }
          : { ...item }
      );
      gameResultStore.setGameResults(updatedResults);
    }
    if (notificationStore.notificationCode !== BetResponseCode.UnderMaintenanceNote) {
      notificationStore.setNotificationType(null);
    }

    if (loadingStore.isPixiLoaded) {
      soundService.play(soundTypes.tryingToCatch);
      gameAnimations.wolfCatching().then(() => {
        gameAnimations.killBackgroundLoop();
        gameAnimations.wolfFirstPart();
      });
    }
    const totalBetInRoundStart = doBetStore.pigs.reduce((acc, curr) => {
      if (curr.betAmount) {
        acc += curr.betAmount;
      }
      return acc;
    }, 0);
    totalBetStore.setTotalBetForNote(totalBetInRoundStart);
  }

  handleBetsClosed() {
    doBetStore.setBetAmountPopupId(null);
    oddStore.setIsShowCloud(true);
    reBetStore.setIsReBetClick(false);
    winnersStore.resetWinnersData([]);
    doBetStore.handleBetsClosed();
    roundInfoStore.setSecond(0);
    gameStore.setIsBetsClosed(true);
    roundTimeStore.setTime(null);
    if (loadingStore.isPixiLoaded) {
      delay(() => {
        gameAnimations.whiteWolf();
        gameAnimations.doorOpen();
        soundService.play(soundTypes.openAndLaugh);
        gameAnimations.wolfDoorOpen().then(() => {
          gameAnimations.wolfRun();
        });
      }, 500);

      this.delayCancellation = delay(() => {
        gameAnimations.backgroundLoop();
        gameAnimations.bluePigRunStart().then(() => {
          gameAnimations.runPigByIndex(pigIndexes.blue);
        });
        gameAnimations.redPigRunStart().then(() => {
          gameAnimations.runPigByIndex(pigIndexes.red);
        });
        gameAnimations.yellowPigRunStart().then(() => {
          gameAnimations.runPigByIndex(pigIndexes.yellow);
        });
      }, 2000);
    }
  }

  handleCashOutResult(cashOutData) {
    if (!freeBetStore.activeFreeBet?.id) {
      doBetStore.resetPigFreeBetId(cashOutData?.additionalParams.index);
    }
    freeBetStore.freeBetTotalWinCalculator(cashOutData);
    doBetStore.setWinData(cashOutData);
    if (notificationStore.notificationCode !== BetResponseCode.UnderMaintenanceNote) {
      notificationStore.setNotificationType(null);
    }
  }

  handleReconnecting() {
    GameService.ReconnectCount += 1;
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    loadingStore.updateLoading({ isPixiLoaded: false });
    loadingStore.updateLoading({ isGameInitialLoaded: false });
    if (popupStore.popupName !== PopUps.ConnectionError) {
      gameAnimations.killBackgroundLoop();
      backgroundDrawer.clearTracks();
      totalBetStore.setTotalBet(0);
      popupStore.toggleReconnect(true);
      popupStore.open({ name: PopUps.Reconnect });
      gameDrawer
        .execute({
          mode: 'web',
          scene: 'Main',
          prevScene: null,
        })
        .then(() => {
          gameLiveConnection.sendGameGetInitialState(false).then(() => {});
        });
    }
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }
}

export const gameService = new GameService();
